import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { Node } from '../../../core/models/scene';
import i18n from '../../../i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';
import { SessionMetrics } from '../../analytics/services/SessionMetrics';

const PERSON_SIZE = 60;
const SPACING = 120;

export class AddParentTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-add-parent',
    name: i18n.t('genogram:tools.addParent', { defaultValue: 'Add Parent' }) as string,
    icon: '👨',
    category: 'create',
    description: 'Add a parent to the selected person',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    applicability: {
      contextTypes: ['person'],
      diagramTypes: ['genogram'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    },
    shortcuts: [
      { key: '3', description: 'Add parent (when one person selected)' }
    ]
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.addParent', 'Add Parent');
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;

    // Check that selection is a single person
    const summary = context.selectionSummary;
    return summary?.contextType === 'person';
  }

  execute(): ICommand | null {
    if (!this.context) return null;

    const selectedId = Array.from(this.context.selection)[0];
    const state = useDiagramStore.getState();
    const selectedNode = state.nodes.find(n => n.id === selectedId);

    if (!selectedNode) return null;

    const pos = { x: selectedNode.x, y: selectedNode.y };
    const size = {
      width: (selectedNode as any).w ?? (selectedNode as any).width ?? PERSON_SIZE,
      height: (selectedNode as any).h ?? (selectedNode as any).height ?? PERSON_SIZE,
    } as { width: number; height: number };

    // Position parent above
    const newNodeId = `person-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNode: Node = {
      id: newNodeId,
      type: 'person',
      name: i18n.t('genogram:relationships.parent', { defaultValue: 'Parent' }) as string,
      sex: 'unknown',
      x: pos.x,
      y: pos.y - size.height - SPACING,
      w: size.width,
      h: size.height,
      width: size.width,
      height: size.height,
      category: 'genogram',
    } as Node;

    const nodeCommand = new CreateNodeCommand(newNode);

    // For parent-child relationship, the edge goes from parent to child
    const edgeCommand = new CreateEdgeCommand({
      type: 'child',
      from: newNodeId,
      to: selectedId,
      category: 'genogram',
      fromAnchor: 'bottom',
      toAnchor: 'top',
      attrs: { __captionHidden: true },
    });

    // Track quick-add usage (aggregated + first-time)
    SessionMetrics.increment('quick_adds');
    SessionMetrics.firstTime('quick_add');
    
    return new CompositeCommand('Add Parent', [nodeCommand, edgeCommand]);
  }
}
